import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import NetInfo from '@react-native-community/netinfo';
import { SyncManager } from '../lib/sync-manager';
import type { SyncApi } from '../lib/sync-manager';
import { UserPrefsSync, type UserPrefsApi } from '../lib/user-prefs-sync';
import { trpc } from '../lib/trpc';
import { useAuth } from '../contexts/AuthContext';
import { useAppBackground } from '../hooks/useAppLifecycle';
import { useReferenceSuggester } from '../hooks/useReferenceSuggester';
import { useItineraryStore, getTripEntityIds } from '../store/itinerary-store';

/** Serialize the subset of notesMap belonging to a trip into a JSON string. */
function serializeNotesForTrip(tripId: string): string | undefined {
  const { trips, notesMap } = useItineraryStore.getState();
  const trip = trips.find((t) => t.id === tripId);
  if (!trip) return undefined;
  const entityIds = getTripEntityIds(trip);
  const tripNotes: Record<string, string> = {};
  for (const id of entityIds) {
    const note = notesMap[id];
    if (note) tripNotes[id] = note;
  }
  return Object.keys(tripNotes).length > 0 ? JSON.stringify(tripNotes) : undefined;
}

/**
 * Wires the SyncManager singleton to the tRPC client, installs lifecycle
 * listeners that flush pending writes before the tab closes / app
 * backgrounds, and hydrates from the server when the user is authenticated.
 * Render once inside the trpc.Provider tree.
 */
export function SyncManagerInit() {
  const utils = trpc.useUtils();
  const { isAuthenticated } = useAuth();
  const initializedRef = useRef(false);

  // Once-only init: API + idempotent hydrate-wrapper for note merging +
  // lifecycle listeners. Re-running on auth toggle would double-wrap the
  // hydrateFromServer monkey-patch; keep it gated by initializedRef.
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const api: SyncApi = {
      createTrip: (trip) => {
        const notes = serializeNotesForTrip(trip.id);
        return utils.client.trips.create.mutate({ ...trip, notes });
      },
      updateTrip: (tripId, updates) => {
        const resolved = { ...updates };
        if ('notes' in resolved) {
          resolved.notes = serializeNotesForTrip(tripId);
        }
        return utils.client.trips.update.mutate({ tripId, updates: resolved });
      },
      deleteTrip: async (tripId) => {
        await utils.client.trips.delete.mutate({ tripId });
      },
      listTrips: () => utils.client.trips.list.query(),
    };

    const sm = SyncManager.getInstance();
    sm.init(api);

    // User-prefs sync — separate singleton because the data model is
    // simpler (one record per user, ship the whole thing on each flush).
    const userPrefsApi: UserPrefsApi = {
      get: () => utils.client.userPrefs.get.query(),
      update: (prefs) => utils.client.userPrefs.update.mutate(prefs),
    };
    UserPrefsSync.getInstance().init(userPrefsApi);

    // Subscribe to dirty-flag flips and debounce a flush. We watch the
    // flag itself rather than every pref slice so any new synced pref
    // automatically participates as long as its action calls
    // markUserPrefsDirty(). Consent (ADR-0065) rides this same flag —
    // `consent-store.setConsent` calls markUserPrefsDirty directly.
    const unsubscribePrefs = useItineraryStore.subscribe((state, prev) => {
      if (state.dirtyUserPrefs && !prev.dirtyUserPrefs) {
        UserPrefsSync.getInstance().scheduleFlush();
      }
    });

    // Wrap hydrateFromServer once so we ALSO read legacy top-level
    // Trip.notes (a JSON blob of {entityId: noteText}) into the local
    // notesMap. Pre-inline-notes-fix this was the only sync path; new
    // writes go through the days inline fields and the original
    // hydrate handles those. We keep this branch for backwards-compat
    // with records that haven't been re-flushed yet.
    //
    // CRITICAL: must be local-wins (spread order: server first, local
    // second). The previous server-wins ordering clobbered any note
    // the user typed locally between flushes — including notes already
    // persisted in AsyncStorage but not yet pushed to the server. This
    // mirrors the local-wins merge already used inside hydrateFromServer
    // for the inline-notes path.
    const originalHydrate = sm.hydrateFromServer.bind(sm);
    sm.hydrateFromServer = async (opts) => {
      const result = await originalHydrate(opts);
      if (!result.upserted) return result;
      const { trips } = useItineraryStore.getState();
      const merged: Record<string, string> = {};
      for (const trip of trips) {
        if (trip.notes) {
          try {
            const tripNotes = JSON.parse(trip.notes) as Record<string, string>;
            Object.assign(merged, tripNotes);
          } catch { /* ignore malformed */ }
        }
      }
      if (Object.keys(merged).length > 0) {
        useItineraryStore.setState((s) => ({
          notesMap: { ...merged, ...s.notesMap },
        }));
      }
      return result;
    };

    // ---- Lifecycle listeners ----
    // The single biggest reliability fix: flush any pending mutations before
    // the tab closes / app backgrounds. flushImmediate cancels the 2-second
    // debounce so the write doesn't get dropped.

    const flushNow = (): void => {
      void sm.flushImmediate();
      void UserPrefsSync.getInstance().flushImmediate();
    };

    // Bypass the dirty-queue BACKOFF the moment connectivity returns —
    // otherwise the user can wait up to 60s for the next retry tick after
    // coming back online. Pair with hydrateFromServer so any remote edits
    // made on another device while we were offline land immediately too.
    const handleOnline = (): void => {
      void sm.flushImmediate();
      void sm.hydrateFromServer({ force: true });
      void UserPrefsSync.getInstance().flushImmediate();
    };

    let netInfoUnsubscribe: (() => void) | null = null;
    let wasOnline = true;

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      // pagehide is the only one Safari fires reliably for tab close;
      // beforeunload covers desktop browsers and route navigation.
      window.addEventListener('pagehide', flushNow);
      window.addEventListener('beforeunload', flushNow);
      window.addEventListener('online', handleOnline);
      wasOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
    } else if (Platform.OS !== 'web') {
      // Native: NetInfo emits on every connectivity change. Fire the
      // reconnect handler only on offline→online transitions so we don't
      // spam flushes during a flaky Wi-Fi handshake.
      netInfoUnsubscribe = NetInfo.addEventListener((state) => {
        const isOnline =
          state.isConnected !== false && state.isInternetReachable !== false;
        if (isOnline && !wasOnline) handleOnline();
        wasOnline = isOnline;
      });
    }
    // AppState (background / inactive) is wired separately via
    // useAppBackground below so this useEffect doesn't have to manage two
    // unrelated subscription lifetimes.

    // Dev console bridge: lets us ask a user "open devtools and run
    // __kiipsSync.dirty()" the next time the bug pops up.
    if (typeof globalThis !== 'undefined' && (process.env.NODE_ENV !== 'production')) {
      (globalThis as { __kiipsSync?: unknown | undefined}).__kiipsSync = {
        status: () => sm.getStatus(),
        lastError: () => sm.getLastError(),
        dirty: () => (useItineraryStore.getState() as unknown as { dirtyTrips: unknown }).dirtyTrips,
        pendingDeletes: () => (useItineraryStore.getState() as unknown as { pendingDeletes: unknown }).pendingDeletes,
        flush: () => sm.flushImmediate(),
        hydrate: () => sm.hydrateFromServer({ force: true }),
      };
    }

    return () => {
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.removeEventListener('pagehide', flushNow);
        window.removeEventListener('beforeunload', flushNow);
        window.removeEventListener('online', handleOnline);
      }
      if (netInfoUnsubscribe) netInfoUnsubscribe();
      unsubscribePrefs();
    };
  }, [utils]);

  // Flush before the OS may suspend our JS engine. Cross-platform via
  // AppState; complements the web-only pagehide/beforeunload above.
  // Both managers flush in parallel — a pref toggle inside the 1.5s
  // UserPrefsSync debounce would otherwise be dropped when the OS kills
  // the JS engine, leaving local Zustand-persisted state ahead of the
  // server until the next foreground edit.
  useAppBackground(() => {
    void SyncManager.getInstance().flushImmediate();
    void UserPrefsSync.getInstance().flushImmediate();
  });

  // Auth-driven inbound hydrate + outbound flush. Re-runs whenever the user
  // transitions to authenticated. The flush picks up any dirty entries that
  // were paused on a 401, which now have a fresh access token to send with.
  useEffect(() => {
    if (!isAuthenticated) return;
    const sm = SyncManager.getInstance();
    void sm.hydrateFromServer({ force: true });
    void sm.flushImmediate();

    // Force the server-backed user prefs (currency, units, time format,
    // nationality, home, theme) to refetch on every authentication.
    // These are read with staleTime: Infinity and the whole React Query
    // cache is persisted to IndexedDB / AsyncStorage, so without this a
    // browser keeps serving its own stale snapshot and never sees a
    // change made on another device until the 24h cache window expires.
    // Mirrors the forced trip hydrate above. invalidate() keeps the
    // current data on screen while it refetches, so no flash to defaults.
    void utils.userPrefs.get.invalidate();
    // User prefs follow the same auth-cycle: pull the server record on
    // sign-in, push any pending local edits that were paused on a 401.
    //
    // CRITICAL: hydrate MUST complete before flushImmediate (ADR-0065).
    // An anonymous user can set `consent` locally before sign-in, leaving
    // `dirtyUserPrefs: true`. If we flushed in parallel with hydrate and
    // the flush won the race, the anonymous decision would overwrite a
    // pre-existing server decision from another device — violating the
    // "server wins on conflict" rule. Sequencing hydrate first lets
    // applyServer install the server's record locally; the subsequent
    // flush then ships only the actual delta (or is a no-op).
    const ups = UserPrefsSync.getInstance();
    void (async () => {
      await ups.hydrate({ force: true });
      await ups.flushImmediate();
    })();
  }, [isAuthenticated, utils]);

  // Background reference suggester — fires `references.suggestForTrip`
  // for any trip without `references` yet, appends results via the
  // store action (which de-dupes and marks the field dirty so it
  // round-trips through the normal sync).
  useReferenceSuggester();

  return null;
}
